import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MenuOptions, TOP_MENUS } from '../lib/screenMenus';
import { useSearchParams } from 'react-router-dom';
import {
  IMPROVE_STATUS,
  api,
  fetchImprovementFileUrl,
  uploadImprovementFile,
  type ImproveDetail,
  type ImproveFile,
  type ImproveLink,
  type ImproveLinkProvider,
  type ImproveMe,
  type ImproveRow,
  type ImproveStatus,
} from '../lib/api';
import { ErrorBox, Pager, usePageSize, wallClock } from '../components/DataTable';
import Modal from '../components/Modal';
import { FitBox } from '../components/FitBox';
import { useAutoSearch } from '../lib/search';
import { importLocalSeen } from '../lib/improveSeen';

/**
 * 개선요청 게시판 (2026-09-18 사용자 요청 — "다른 프로젝트 참조해서 모든 기능").
 *
 * 다른 네 프로젝트(dyops·callcenter·dibm·dgmkt)의 게시판을 한 화면으로 합친 것이다.
 * 저장소만 다르다 — 저쪽은 Firestore 문서 하나에 `history[]` 를 쌓고, 여기는 표 3개다
 * (`db/schema/_improvement.sql`). 권한은 전부 서버가 강제한다(여기 버튼은 안내일 뿐이다).
 *
 * 화면 규칙
 * - 🔴 **`요청` 상태를 목록에서 빼지 않는다.** 요청자가 "다시 봐 주세요"를 표시할 유일한 통로다
 *   (dyops 는 그 통로가 없어 게시판이 «전건 완료»로 보였다).
 * - 🔴 **완료로 닫는 것은 관리자만.** 요청자가 닫으면 미처리 목록에서 사라져 아무도 못 본다.
 * - 🔴 코멘트와 상태 전이를 **한 번에** 보낸다 — 나눠 보내면 구글챗 알림이 두 번 나간다.
 * - 팝업은 `Modal` 이라 바깥을 눌러도 안 닫힌다(쓰던 글이 사라지지 않게).
 * - 구글시트·문서는 **파일이 아니라 링크**로 붙인다(`improvement_link`) — 바이트를 우리가 쥐지 않는다.
 *   ⚠️ 그래서 **열람 권한도 우리가 아니라 구글이 쥔다.** 링크만 붙이고 공유를 안 열면 상대는 못 본다.
 */


/** 대상 화면 — 상단 메뉴 라벨 그대로. "어느 화면 이야기인가"가 가장 먼저 필요한 정보다. */
// 화면 고르기 목록 = `lib/screenMenus.tsx`(상단 메뉴·하위 탭 이름 그대로 · 2026-10-19 way).
const MENUS = TOP_MENUS;

const STATUS_ORDER: ImproveStatus[] = ['requested', 'reviewing', 'in_progress', 'done', 'rejected'];

function StatusChip({ status }: { status: ImproveStatus }) {
  const s = IMPROVE_STATUS[status];
  return <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${s.chip}`}>{s.label}</span>;
}

function fileEmoji(contentType: string): string {
  return contentType.startsWith('image/') ? '🖼' : contentType === 'application/pdf' ? '📕' : '📄';
}

function sizeText(bytes: string | number): string {
  const n = Number(bytes);
  if (!Number.isFinite(n)) return '';
  return n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`;
}

/** 첨부 칩. 클릭하면 새 탭 — 열람에도 로그인 토큰이 필요해서 `<a href>` 로는 못 건다. */
function FileChip({
  file,
  canRemove,
  onRemove,
}: {
  file: ImproveFile;
  canRemove: boolean;
  onRemove: () => void;
}) {
  const [busy, setBusy] = useState(false);
  async function open() {
    setBusy(true);
    try {
      const url = await fetchImprovementFileUrl(file.improvement_file_id);
      window.open(url, '_blank', 'noopener');
      // 탭이 읽고 나면 해제한다 — 안 하면 이 탭이 파일을 통째로 물고 있는다.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  const chip = (
    <span className="inline-flex items-center gap-1 rounded-lg border border-line bg-canvas px-2 py-1 text-xs">
      <button className="max-w-[16rem] truncate text-left hover:underline" disabled={busy} onClick={open}>
        {fileEmoji(file.content_type)} {file.file_name}
      </button>
      <span className="text-ink-faint">{sizeText(file.byte_size)}</span>
      {canRemove && (
        <button className="text-ink-faint hover:text-red-600" title="첨부 내리기" onClick={onRemove}>
          ×
        </button>
      )}
    </span>
  );
  // #363 — 이미지는 칩만 두지 말고 본문 안에 바로 보이게(누르면 원본을 새 탭에).
  if (!String(file.content_type ?? '').startsWith('image/')) return chip;
  return (
    <div className="w-full space-y-1">
      <InlineImage fileId={file.improvement_file_id} name={file.file_name} onOpen={open} />
      {chip}
    </div>
  );
}

/** 첨부 이미지를 그 자리에 그린다. 열람에 로그인 토큰이 필요해 blob 주소로 받아 쓰고, 화면을 떠나면 놓는다. */
function InlineImage({ fileId, name, onOpen }: { fileId: number; name: string; onOpen: () => void }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url: string | null = null;
    let alive = true;
    fetchImprovementFileUrl(fileId)
      .then((u) => {
        url = u;
        if (alive) setSrc(u);
        else URL.revokeObjectURL(u);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileId]);
  if (failed) return null;
  if (!src) return <div className="h-24 w-full max-w-md animate-pulse rounded-lg bg-canvas" />;
  return (
    <button type="button" className="block max-w-full" title="원본 크게 보기" onClick={onOpen}>
      <img src={src} alt={name} className="max-h-[32rem] max-w-full rounded-lg border border-line object-contain" />
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 링크 첨부 (2026-09-21 — "구글시트도 입력할 수 있도록")
// ─────────────────────────────────────────────────────────────────────────────

const LINK_ICON: Record<ImproveLinkProvider, string> = {
  sheet: '📊',
  doc: '📝',
  slide: '📽',
  form: '📋',
  drive: '📁',
  link: '🔗',
};

const LINK_NAME: Record<ImproveLinkProvider, string> = {
  sheet: '구글 시트',
  doc: '구글 문서',
  slide: '구글 슬라이드',
  form: '구글 설문',
  drive: '구글 드라이브',
  link: '링크',
};

/** `<a href>` 에 꽂아도 되는 주소인가. 🔴 **정본은 서버**(`normalizeBoardLink`) — 여기는 즉시 안내용이다. */
function isHttpUrl(v: string): boolean {
  try {
    const u = new URL(v);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * 종류 짐작 — **아이콘과 기본 이름에만** 쓴다(저장되는 값은 서버가 다시 판정한다).
 * 여기서 틀려도 등록 뒤 새로고침하면 서버 값으로 맞춰진다.
 */
function guessProvider(v: string): ImproveLinkProvider {
  try {
    const u = new URL(v);
    const host = u.hostname.toLowerCase();
    const path = u.pathname.toLowerCase();
    if (host === 'docs.google.com') {
      if (path.startsWith('/spreadsheets')) return 'sheet';
      if (path.startsWith('/document')) return 'doc';
      if (path.startsWith('/presentation')) return 'slide';
      if (path.startsWith('/forms')) return 'form';
    }
    if (host === 'sheets.google.com') return 'sheet';
    if (host === 'drive.google.com') return 'drive';
  } catch {
    // 형식은 등록할 때 막는다.
  }
  return 'link';
}

/** 등록 전 링크 후보. 첨부파일과 달리 바이트가 없어 취소해도 남는 것이 없다. */
interface PendingLink {
  url: string;
  title: string;
}

/**
 * 링크 칩. 첨부와 달리 **평범한 `<a>`** 다 — 파일은 우리 버킷이라 토큰이 필요했지만
 * 이 주소의 권한은 구글이 쥔다(우리가 대신 열어 줄 수 없고, 열어 줘서도 안 된다).
 */
function LinkChip({ link, canRemove, onRemove }: { link: ImproveLink; canRemove: boolean; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-lg border border-line bg-canvas px-2 py-1 text-xs">
      <a
        className="max-w-[18rem] truncate hover:underline"
        href={link.link_url}
        target="_blank"
        rel="noopener noreferrer"
        title={link.link_url}
      >
        {LINK_ICON[link.provider] ?? '🔗'} {link.link_title}
      </a>
      {canRemove && (
        <button className="text-ink-faint hover:text-red-600" title="링크 내리기" onClick={onRemove}>
          ×
        </button>
      )}
    </span>
  );
}

/**
 * 링크 붙이기 입력줄. 🔴 `<label>` 을 쓰지 않는다 — 이 줄이 통째로 폼 안에 들어가는데
 * `<label>` 이 겹치면 클릭이 엉뚱한 입력으로 되쏘인다(`pnpm web:label` 이 검사하는 부류).
 * 주소를 붙여 넣고 Enter 만 쳐도 담기게 한다(이름은 선택 — 비우면 서버가 «구글 시트»로 채운다).
 */
function LinkComposer({ onAdd }: { onAdd: (link: PendingLink) => void }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [err, setErr] = useState<string | null>(null);

  function add() {
    const u = url.trim();
    if (!isHttpUrl(u)) {
      setErr('https:// 로 시작하는 주소를 붙여 넣어 주세요.');
      return;
    }
    onAdd({ url: u, title: title.trim() });
    setUrl('');
    setTitle('');
    setErr(null);
    setOpen(false);
  }

  if (!open) {
    return (
      <button className="btn-ghost" onClick={() => setOpen(true)}>
        🔗 링크 첨부
      </button>
    );
  }
  return (
    <div className="w-full rounded-xl border border-line bg-canvas p-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          className="field min-w-[16rem] flex-1"
          autoFocus
          placeholder="구글시트·문서 주소를 붙여 넣으세요 (https://docs.google.com/…)"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setErr(null);
          }}
          onKeyDown={(e) => e.key === 'Enter' && add()}
        />
        <input
          className="field w-44"
          placeholder="이름(선택)"
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
        />
        <button className="btn-primary" onClick={add}>
          담기
        </button>
        <button
          className="btn-ghost"
          onClick={() => {
            setOpen(false);
            setErr(null);
          }}
        >
          취소
        </button>
      </div>
      {err ? (
        <div className="mt-1 text-xs text-red-600">{err}</div>
      ) : (
        // 🔴 가장 흔한 실패는 주소가 아니라 **공유 설정**이다 — 우리가 대신 열어 줄 수 없으니 미리 말한다.
        <div className="mt-1 text-xs text-ink-faint">
          시트 공유가 닫혀 있으면 담당자가 열지 못합니다 — 구글 문서에서 사내 사용자에게 열어 주세요.
        </div>
      )}
    </div>
  );
}

/** 등록 전 링크 목록. 파일과 나란히 보이게 같은 칩 모양을 쓴다. */
function PendingLinks({ links, onDrop }: { links: PendingLink[]; onDrop: (i: number) => void }) {
  if (!links.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {links.map((l, i) => {
        const p = guessProvider(l.url);
        return (
          <span
            key={`${l.url}-${i}`}
            className="inline-flex items-center gap-1 rounded-lg border border-line bg-canvas px-2 py-1 text-xs"
          >
            {LINK_ICON[p]} <span className="max-w-[18rem] truncate">{l.title || LINK_NAME[p]}</span>
            <button className="text-ink-faint hover:text-red-600" onClick={() => onDrop(i)}>
              ×
            </button>
          </span>
        );
      })}
    </div>
  );
}

/**
 * 붙여넣기·파일선택으로 모은 첨부 후보. 등록 **전에는 서버에 올리지 않는다** —
 * 글을 취소했는데 파일만 남으면 주인 없는 객체가 쌓인다.
 */
function PendingFiles({
  files,
  onDrop,
}: {
  files: File[];
  onDrop: (i: number) => void;
}) {
  // 붙여넣은 캡처는 올리기 전에도 그림으로 보인다(10-21 way 「클립보드 붙여넣어도 파일로만 보인다」).
  const urls = useMemo(() => files.map((f) => (f.type.startsWith('image/') ? URL.createObjectURL(f) : null)), [files]);
  useEffect(() => () => urls.forEach((u) => u && URL.revokeObjectURL(u)), [urls]);
  if (!files.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {files.map((f, i) => urls[i] ? (
        <span key={`${f.name}-${i}`} className="relative inline-block rounded-lg border border-line bg-canvas p-1">
          <img src={urls[i]!} alt={f.name} className="block max-h-48 max-w-full rounded" />
          <button className="absolute right-1 top-1 rounded bg-white/90 px-1.5 text-xs text-ink-faint shadow hover:text-red-600"
            title="빼기" onClick={() => onDrop(i)}>
            ×
          </button>
        </span>
      ) : (
        <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 rounded-lg border border-line bg-canvas px-2 py-1 text-xs">
          {fileEmoji(f.type)} <span className="max-w-[14rem] truncate">{f.name}</span>
          <span className="text-ink-faint">{sizeText(f.size)}</span>
          <button className="text-ink-faint hover:text-red-600" onClick={() => onDrop(i)}>
            ×
          </button>
        </span>
      ))}
    </div>
  );
}

/**
 * 팝업 본문 껍데기. `Modal` 의 첫 자식은 **컴포넌트여야 한다**(`pnpm web:modal` 가 검사한다) —
 * 예전에 폼을 일괄로 감싸다 엉뚱한 블록에 붙어 오버레이에 글자 한 줄만 뜬 적이 있어서다.
 */
function WriteForm({ children }: { children: React.ReactNode }) {
  return <div className="card p-5">{children}</div>;
}

function DetailShell({ children }: { children: React.ReactNode }) {
  return <div className="card p-5">{children}</div>;
}

/**
 * 본문 안 그림 자리(10-21 way 「내용 안에 들어갈 수 없어? DYOPS 개선요청처럼」).
 * 본문은 평문 그대로 두고 그림 자리만 `[그림 N]` 한 줄로 적는다 — N 은 그 글 요청 첨부 중 그림의 순번(올린 순서).
 * 글쓰기 칸에서 붙여넣은 캡처를 먼저 올리므로 1..k 가 본문 그림이다. 구글챗 카드에도 글자로 읽힌다.
 */
const IMG_MARK = /\[그림 (\d+)\]/g;

/** 본문 + 그 자리의 그림. 표시한 그림 id 는 아래 첨부 줄에서 빼려고 돌려준다. */
function ContentWithImages({ content, images }: { content: string; images: ImproveFile[] }) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  for (const m of content.matchAll(IMG_MARK)) {
    const f = images[Number(m[1]) - 1];
    if (!f) continue;
    parts.push(content.slice(last, m.index));
    parts.push(
      <span key={`img-${m.index}`} className="my-1 block">
        <InlineImage fileId={f.improvement_file_id} name={f.file_name} onOpen={() => openImprovementFile(f.improvement_file_id)} />
      </span>,
    );
    last = (m.index ?? 0) + m[0].length;
  }
  parts.push(content.slice(last));
  return <>{parts}</>;
}

function usedImageIds(content: string, images: ImproveFile[]): Set<number> {
  const ids = new Set<number>();
  for (const m of content.matchAll(IMG_MARK)) {
    const f = images[Number(m[1]) - 1];
    if (f) ids.add(f.improvement_file_id);
  }
  return ids;
}

async function openImprovementFile(fileId: number) {
  try {
    const url = await fetchImprovementFileUrl(fileId);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    alert(e instanceof Error ? e.message : String(e));
  }
}

/**
 * 글쓰기 내용 칸 — 붙여넣은 캡처가 커서 자리에 그림으로 들어간다. 글자는 글자로만 붙는다(서식 없음).
 * 바깥에는 평문(그림 자리 = `[그림 N]`)과 그 순서의 그림 파일 목록만 알린다. 올리기 전엔 서버에 안 올린다.
 */
function ContentEditor({
  initial,
  placeholder,
  onChange,
}: {
  initial: string;
  placeholder: string;
  onChange: (text: string, images: File[]) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const filesRef = useRef(new Map<string, File>());
  const [empty, setEmpty] = useState(!initial);
  useEffect(() => {
    if (ref.current) ref.current.innerText = initial;
    // 처음 한 번만 — 이후는 칸이 정본이다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function emit() {
    const el = ref.current;
    if (!el) return;
    const images: File[] = [];
    let text = '';
    const walk = (n: Node) => {
      if (n.nodeType === Node.TEXT_NODE) { text += n.textContent ?? ''; return; }
      if (!(n instanceof HTMLElement)) return;
      if (n.tagName === 'BR') { text += '\n'; return; }
      if (n.tagName === 'IMG') {
        const f = filesRef.current.get(n.dataset.k ?? '');
        if (f) { images.push(f); text += `${text && !text.endsWith('\n') ? '\n' : ''}[그림 ${images.length}]\n`; }
        return;
      }
      const block = n !== el && (n.tagName === 'DIV' || n.tagName === 'P');
      if (block && text && !text.endsWith('\n')) text += '\n';
      n.childNodes.forEach(walk);
    };
    walk(el);
    setEmpty(!text.trim() && !images.length);
    onChange(text.replace(/\n{3,}/g, '\n\n'), images);
  }

  function onPaste(e: React.ClipboardEvent) {
    const imgs = Array.from(e.clipboardData?.items ?? [])
      .filter((it) => it.kind === 'file')
      .map((it) => it.getAsFile())
      .filter((f): f is File => !!f && f.type.startsWith('image/'));
    e.preventDefault();
    if (!imgs.length) {
      document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
      emit();
      return;
    }
    for (const f of imgs) {
      const k = `${Date.now()}-${Math.random()}`;
      filesRef.current.set(k, f);
      const src = URL.createObjectURL(f);
      document.execCommand('insertHTML', false,
        `<img src="${src}" data-k="${k}" style="display:block;max-height:16rem;max-width:100%;margin:4px 0;border-radius:6px" /><br>`);
    }
    emit();
  }

  return (
    <div className="relative mt-1">
      {empty && <div className="pointer-events-none absolute left-3 top-2 text-sm text-ink-faint">{placeholder}</div>}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        className="field min-h-40 max-h-[50vh] overflow-auto whitespace-pre-wrap break-keep"
        onInput={emit}
        onPaste={onPaste}
      />
    </div>
  );
}

/** Ctrl+V 로 붙인 이미지를 첨부 후보로 받는다(캡처를 저장했다 올리는 수고를 없앤다). */
function usePasteFiles(add: (files: File[]) => void) {
  return useCallback(
    (e: React.ClipboardEvent) => {
      const items = Array.from(e.clipboardData?.items ?? []);
      const files = items
        .filter((it) => it.kind === 'file')
        .map((it) => it.getAsFile())
        .filter((f): f is File => !!f);
      if (!files.length) return;
      // 바이너리가 textarea 에 글자로 떨어지지 않게.
      e.preventDefault();
      add(files);
    },
    [add],
  );
}

export default function Improve() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState<ImproveStatus | ''>('');
  const [mine, setMine] = useState(false);
  const [menu, setMenu] = useState('');
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [PAGE, setPAGE] = usePageSize('improve', 30);
  const [rows, setRows] = useState<ImproveRow[]>([]);
  const [counts, setCounts] = useState<Partial<Record<ImproveStatus, number>>>({});
  const [me, setMe] = useState<ImproveMe | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [writing, setWriting] = useState(false);
  const [tick, setTick] = useState(0);

  useAutoSearch(term, (v) => {
    setQ(v);
    setPage(0);
  });

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    importLocalSeen()
      .then(() => api.listImprovements({ status, mine, menu: menu || undefined, q, limit: PAGE, offset: page * PAGE }))
      .then((r) => {
        if (cancelled) return;
        setRows(r.rows);
        setCounts(r.counts);
        setMe(r.me);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [status, mine, menu, q, page, PAGE, tick]);

  // 알림(구글챗 카드)에서 오는 딥링크 `?open=<번호>`.
  // 🔴 연 뒤 파라미터를 지운다 — 안 지우면 새로고침할 때마다 같은 글이 다시 열린다.
  useEffect(() => {
    const raw = params.get('open');
    if (!raw) return;
    const id = Number(raw);
    if (Number.isInteger(id) && id > 0) setOpenId(id);
    const next = new URLSearchParams(params);
    next.delete('open');
    setParams(next, { replace: true });
  }, [params, setParams]);

  const total = useMemo(() => STATUS_ORDER.reduce((n, s) => n + (counts[s] ?? 0), 0), [counts]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="page-head min-w-0 flex-1">
          <h1 className="font-bold">개선요청</h1>
          <p className="mt-1 text-sm text-ink-soft">
            쓰면서 불편한 점·바꿨으면 하는 것을 올려 주세요. 담당자가 확인하면 상태와 답변이 여기에 붙습니다.
          </p>
        </div>
        <div className="btn-row-sm ml-auto flex gap-2">
          <button className="btn-ghost" onClick={reload} disabled={loading}>
            ↻ 새로고침
          </button>
          <button className="btn-primary" onClick={() => setWriting(true)}>
            ＋ 요청 올리기
          </button>
        </div>
      </div>

      <div className="card px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <button
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${
              status === '' ? 'border-brand-600 bg-brand-600 text-white' : 'border-line bg-surface text-ink-soft'
            }`}
            onClick={() => {
              setStatus('');
              setPage(0);
            }}
          >
            전체 {total}
          </button>
          {STATUS_ORDER.map((s) => (
            <button
              key={s}
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                status === s ? 'border-brand-600 bg-brand-600 text-white' : IMPROVE_STATUS[s].chip
              }`}
              onClick={() => {
                setStatus(s);
                setPage(0);
              }}
            >
              {IMPROVE_STATUS[s].label} {counts[s] ?? 0}
            </button>
          ))}

          <label className="ml-2 flex items-center gap-1 text-sm text-ink-soft">
            <input
              type="checkbox"
              checked={mine}
              onChange={(e) => {
                setMine(e.target.checked);
                setPage(0);
              }}
            />
            내 요청만
          </label>

          <select
            className="field w-40"
            value={menu}
            onChange={(e) => {
              setMenu(e.target.value);
              setPage(0);
            }}
          >
            <option value="">화면 전체</option>
            {MENUS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>

          <input
            className="field w-64"
            placeholder="제목·내용·요청자·번호"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
        </div>
      </div>

      {error && <ErrorBox message={error} />}

      <FitBox deps={[rows.length, loading]}>
        {/* 폰(#85): 제목 칸은 `max-w-0` 이라 남는 폭만 받는다 — 좁은 화면에선 보조 칸(대상 화면·등록·답변)을 숨겨 제목에 폭을 준다. */}
        <table className="w-full">
          <thead>
            <tr>
              <th className="th w-14 sm:w-20">번호</th>
              <th className="th w-16 sm:w-20">상태</th>
              <th className="th">제목</th>
              <th className="th hidden w-28 md:table-cell">대상 화면</th>
              <th className="th w-20 sm:w-32">요청자</th>
              <th className="th hidden w-36 md:table-cell">등록</th>
              <th className="th hidden w-24 sm:table-cell">답변·첨부</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const unread = r.unread;
              return (
                <tr
                  key={r.improvement_id}
                  className="cursor-pointer hover:bg-canvas"
                  title="두 번 누르면 열기"
                  onDoubleClick={() => { window.getSelection()?.removeAllRanges(); setOpenId(r.improvement_id); }}
                >
                  <td className="td text-ink-faint">#{r.improvement_id}</td>
                  <td className="td">
                    <StatusChip status={r.status} />
                  </td>
                  <td className="td max-w-0">
                    <div className="flex items-center gap-2">
                      {r.visibility === 'private' && <span title="비밀글">🔒</span>}
                      <span className="truncate font-medium">{r.title}</span>
                      {unread && (
                        <span className="shrink-0 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                          새 답변
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="td hidden text-ink-soft md:table-cell">{r.menu ?? '—'}</td>
                  <td className="td truncate text-ink-soft">{r.requester_name ?? r.requester_email}</td>
                  <td className="td hidden text-ink-soft md:table-cell">{wallClock(r.created_at)}</td>
                  <td className="td hidden text-ink-soft sm:table-cell">
                    {r.history_count > 0 && <span className="mr-2">💬 {r.history_count}</span>}
                    {r.file_count > 0 && <span className="mr-2">📎 {r.file_count}</span>}
                    {r.link_count > 0 && <span>🔗 {r.link_count}</span>}
                  </td>
                </tr>
              );
            })}
            {!rows.length && !loading && (
              <tr>
                <td className="td text-ink-faint" colSpan={7}>
                  {q || status || mine || menu ? '조건에 맞는 요청이 없습니다.' : '아직 올라온 요청이 없습니다.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </FitBox>

      <Pager page={page} setPage={setPage} rowCount={rows.length} pageSize={PAGE} loading={loading} setPageSize={setPAGE} baseSize={30} />

      {writing && (
        <WriteModal
          email={me?.email ?? ''}
          onClose={() => setWriting(false)}
          onSaved={(id) => {
            setWriting(false);
            reload();
            setOpenId(id);
          }}
        />
      )}
      {openId !== null && (
        <DetailModal
          id={openId}
          me={me}
          onClose={() => {
            setOpenId(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 작성
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 임시저장(#153) — 이 브라우저에 사용자별로(같은 PC 를 다른 사람이 써도 안 섞이게). 첨부 파일은 저장하지 않는다(브라우저가 파일을 못 담는다).
 * 올리기가 성공하면 지운다.
 */
interface Draft { title: string; content: string; menu: string; isPrivate: boolean; links: PendingLink[]; savedAt: string }
const draftKey = (email: string) => `businweb.improve.draft.${email.toLowerCase()}`;
function readDraft(email: string): Draft | null {
  if (!email) return null;
  try {
    const d = JSON.parse(localStorage.getItem(draftKey(email)) ?? 'null') as Draft | null;
    return d && typeof d.title === 'string' ? d : null;
  } catch {
    return null;
  }
}
function clearDraft(email: string) {
  try { localStorage.removeItem(draftKey(email)); } catch { /* 못 지워도 글은 올라갔다 */ }
}

function WriteModal({ email, onClose, onSaved }: { email: string; onClose: () => void; onSaved: (id: number) => void }) {
  const [draft] = useState(() => readDraft(email));
  const [title, setTitle] = useState(draft?.title ?? '');
  const [content, setContent] = useState((draft?.content ?? '').replace(IMG_MARK, ''));
  const [menu, setMenu] = useState(draft?.menu ?? '');
  const [isPrivate, setIsPrivate] = useState(draft?.isPrivate ?? false);
  const [files, setFiles] = useState<File[]>([]);
  // 본문 안 그림(붙여넣은 캡처) — 「[그림 N]」 순서대로 먼저 올린다.
  const [inlineImgs, setInlineImgs] = useState<File[]>([]);
  const [editorKey, setEditorKey] = useState(0);
  const [links, setLinks] = useState<PendingLink[]>(draft?.links ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftNote, setDraftNote] = useState<string | null>(
    draft ? `${draft.savedAt}에 임시저장한 글을 불러왔습니다.` : null,
  );
  const addFiles = useCallback((fs: File[]) => setFiles((prev) => [...prev, ...fs]), []);

  function saveDraft() {
    const now = new Date();
    const savedAt = `${now.getMonth() + 1}/${now.getDate()} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    try {
      localStorage.setItem(draftKey(email), JSON.stringify({ title, content: content.replace(IMG_MARK, ''), menu, isPrivate, links, savedAt } satisfies Draft));
      setDraftNote(`${savedAt} 임시저장했습니다. 다음에 「요청 올리기」를 누르면 이어서 쓸 수 있습니다.${files.length || inlineImgs.length ? ' 그림·첨부 파일은 저장되지 않습니다.' : ''}`);
      setError(null);
    } catch {
      setError('임시저장하지 못했습니다. 내용이 너무 길 수 있습니다.');
    }
  }

  function dropDraft() {
    clearDraft(email);
    setTitle('');
    setContent('');
    setInlineImgs([]);
    setEditorKey((k) => k + 1);
    setMenu('');
    setIsPrivate(false);
    setLinks([]);
    setDraftNote(null);
  }

  async function save() {
    if (!title.trim()) {
      setError('제목을 입력하세요.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // 🔴 글을 먼저 만들고 그 번호로 첨부를 올린다(저장 경로에 글 번호가 들어간다).
      const { improvementId } = await api.createImprovement({
        title: title.trim(),
        content: content.trim(),
        menu: menu || null,
        isPrivate,
      });
      // 🔴 본문 그림 먼저 — 「[그림 N]」 은 요청 첨부 그림의 올린 순서다.
      for (const f of [...inlineImgs, ...files]) {
        // 한 장이 실패해도 글은 이미 올라갔다 — 어느 파일이 빠졌는지만 알린다.
        try {
          await uploadImprovementFile(improvementId, 'request', f);
        } catch (e) {
          alert(`${f.name} 을(를) 올리지 못했습니다: ${e instanceof Error ? e.message : String(e)}`);
        }
      }
      for (const l of links) {
        try {
          await api.addImprovementLink({ id: improvementId, kind: 'request', url: l.url, title: l.title || undefined });
        } catch (e) {
          alert(`링크를 붙이지 못했습니다: ${e instanceof Error ? e.message : String(e)}`);
        }
      }
      clearDraft(email);
      onSaved(improvementId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal width="max-w-2xl">
      <WriteForm>
        <h2 className="text-lg font-bold">개선 요청 올리기</h2>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-sm font-medium">제목</label>
            <input className="field mt-1" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
          </div>
          <div>
            <label className="text-sm font-medium">어느 화면인가요</label>
            <select className="field mt-1" value={menu} onChange={(e) => setMenu(e.target.value)}>
              <option value="">고르지 않음</option>
              <MenuOptions current={menu} />
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">내용</label>
            <ContentEditor
              key={editorKey}
              initial={content}
              placeholder="무엇이 불편한지, 어떻게 되면 좋겠는지 적어 주세요. 화면을 캡처해 Ctrl+V 로 붙여 넣으면 그 자리에 들어갑니다."
              onChange={(t, imgs) => { setContent(t); setInlineImgs(imgs); }}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-1 text-sm">
              <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />
              🔒 비밀글 (작성자와 관리자만 봅니다)
            </label>
            <label className="btn-ghost cursor-pointer">
              📎 파일 첨부
              <input
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  addFiles(Array.from(e.target.files ?? []));
                  e.target.value = '';
                }}
              />
            </label>
            <LinkComposer onAdd={(l) => setLinks((p) => [...p, l])} />
          </div>
          <PendingFiles files={files} onDrop={(i) => setFiles((p) => p.filter((_, k) => k !== i))} />
          <PendingLinks links={links} onDrop={(i) => setLinks((p) => p.filter((_, k) => k !== i))} />
          {draftNote && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-canvas px-3 py-2 text-sm text-ink-soft">
              <span>{draftNote}</span>
              <button className="text-xs text-red-600 underline" onClick={dropDraft} disabled={saving}>
                임시저장 지우기
              </button>
            </div>
          )}
          {error && <ErrorBox message={error} />}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose} disabled={saving}>
            취소
          </button>
          {email && (
            <button className="btn-ghost" onClick={saveDraft} disabled={saving}>
              임시저장
            </button>
          )}
          <button className="btn-primary" onClick={save} disabled={saving}>
            {saving ? '올리는 중…' : '올리기'}
          </button>
        </div>
      </WriteForm>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 상세
// ─────────────────────────────────────────────────────────────────────────────

function DetailModal({ id, me, onClose }: { id: number; me: ImproveMe | null; onClose: () => void }) {
  const [data, setData] = useState<ImproveDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState('');
  const [statusTo, setStatusTo] = useState<ImproveStatus | ''>('');
  const [resultNote, setResultNote] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [links, setLinks] = useState<PendingLink[]>([]);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editMenu, setEditMenu] = useState('');
  const addFiles = useCallback((fs: File[]) => setFiles((prev) => [...prev, ...fs]), []);
  const onPaste = usePasteFiles(addFiles);

  const load = useCallback(() => {
    setError(null);
    api
      .getImprovement({ id })
      .then((d) => {
        // 읽음은 서버가 getImprovement 에서 계정 단위로 남긴다(09-29).
        setData(d);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [id]);

  useEffect(load, [load]);

  const row = data?.row ?? null;
  const actor = data?.me ?? me;
  const isAdmin = actor?.isAdmin === true;
  const isOwner = !!row && !!actor && row.requester_email === actor.email;
  // 서버(`improvementWrite.allowedStatuses`)와 **같은 규칙**이다. 여기서 더 열어도 서버가 막는다.
  const statusOptions: ImproveStatus[] = row
    ? isAdmin
      ? STATUS_ORDER.filter((s) => s !== row.status)
      : isOwner
        ? STATUS_ORDER.filter((s) => s !== row.status && s !== 'done')
        : []
    : [];
  const canEdit = isOwner && row?.status === 'requested';

  async function send() {
    if (!row) return;
    if (!comment.trim() && !statusTo && !files.length && !links.length) {
      setError('코멘트를 쓰거나 상태를 선택하세요.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      let historyId: number | null = null;
      if (comment.trim() || statusTo) {
        // 🔴 코멘트·상태·완료결과를 한 번에 — 나눠 부르면 같은 처리에 알림이 두 번 나간다.
        const r = await api.commentImprovement({
          id: row.improvement_id,
          comment: comment.trim() || undefined,
          statusTo: statusTo || '',
          resultNote: statusTo === 'done' ? resultNote.trim() : undefined,
        });
        historyId = r.historyId;
      }
      const kind = statusTo === 'done' && isAdmin ? 'result' : 'comment';
      for (const f of files) {
        await uploadImprovementFile(row.improvement_id, kind, f, historyId ?? undefined);
      }
      for (const l of links) {
        await api.addImprovementLink({
          id: row.improvement_id,
          kind,
          historyId: historyId ?? undefined,
          url: l.url,
          title: l.title || undefined,
        });
      }
      setComment('');
      setStatusTo('');
      setResultNote('');
      setFiles([]);
      setLinks([]);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit() {
    if (!row) return;
    setBusy(true);
    setError(null);
    try {
      await api.patchImprovement({
        id: row.improvement_id,
        title: editTitle.trim(),
        content: editContent.trim(),
        menu: editMenu || null,
      });
      setEditing(false);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function toggleVisibility() {
    if (!row) return;
    const toPrivate = row.visibility !== 'private';
    if (!toPrivate && !confirm('전체공개로 바꾸면 모든 직원이 이 글을 볼 수 있습니다. 바꿀까요?')) return;
    setBusy(true);
    setError(null);
    try {
      await api.setImprovementVisibility({ id: row.improvement_id, isPrivate: toPrivate });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function removePost() {
    if (!row) return;
    if (!confirm(`#${row.improvement_id} 「${row.title}」 을(를) 삭제할까요? 목록에서 사라집니다.`)) return;
    setBusy(true);
    try {
      await api.deleteImprovement({ id: row.improvement_id });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  async function removeFile(fileId: number) {
    setBusy(true);
    try {
      await api.deleteImprovementFile({ fileId });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function removeLink(linkId: number) {
    setBusy(true);
    try {
      await api.deleteImprovementLink({ linkId });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const requestFiles = (data?.files ?? []).filter((f) => f.kind === 'request');
  // 본문 「[그림 N]」 = 요청 첨부 그림의 올린 순서(id 순).
  const requestImages = requestFiles
    .filter((f) => String(f.content_type ?? '').startsWith('image/'))
    .sort((a, b) => a.improvement_file_id - b.improvement_file_id);
  const inlineUsed = usedImageIds(String(row?.content ?? ''), requestImages);
  const resultFiles = (data?.files ?? []).filter((f) => f.kind === 'result');
  const filesOfHistory = (hid: number) => (data?.files ?? []).filter((f) => f.kind === 'comment' && f.history_id === hid);
  const requestLinks = (data?.links ?? []).filter((l) => l.kind === 'request');
  const resultLinks = (data?.links ?? []).filter((l) => l.kind === 'result');
  const linksOfHistory = (hid: number) => (data?.links ?? []).filter((l) => l.kind === 'comment' && l.history_id === hid);

  return (
    <Modal width="max-w-3xl">
      <DetailShell>
        {!row ? (
          <>
            <div className="text-sm text-ink-soft">{error ?? '불러오는 중입니다…'}</div>
            <div className="mt-4 flex justify-end">
              <button className="btn-ghost" onClick={onClose}>
                닫기
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusChip status={row.status} />
              {row.visibility === 'private' && <span className="text-xs text-ink-soft">🔒 비밀글</span>}
              {(isOwner || isAdmin) && (
                <button
                  className="rounded border border-line px-2 py-0.5 text-xs text-ink-soft hover:bg-canvas"
                  onClick={toggleVisibility}
                  disabled={busy}
                  title="작성자와 관리자만 바꿀 수 있습니다"
                >
                  {row.visibility === 'private' ? '전체공개로 바꾸기' : '🔒 비밀글로 바꾸기'}
                </button>
              )}
              <span className="text-xs text-ink-faint">#{row.improvement_id}</span>
              {row.menu && <span className="rounded bg-canvas px-2 py-0.5 text-xs text-ink-soft">{row.menu}</span>}
              <span className="ml-auto text-xs text-ink-soft">
                {row.requester_name ?? row.requester_email} · {wallClock(row.created_at)}
              </span>
            </div>

            {editing ? (
              <div className="mt-3 space-y-2">
                <input className="field" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                <select className="field" value={editMenu} onChange={(e) => setEditMenu(e.target.value)}>
                  <option value="">고르지 않음</option>
                  <MenuOptions current={editMenu} />
                </select>
                <textarea className="field h-40" value={editContent} onChange={(e) => setEditContent(e.target.value)} />
                <div className="flex justify-end gap-2">
                  <button className="btn-ghost" onClick={() => setEditing(false)} disabled={busy}>
                    되돌리기
                  </button>
                  <button className="btn-primary" onClick={saveEdit} disabled={busy}>
                    수정 저장
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="mt-2 text-lg font-bold">{row.title}</h2>
                <div className="mt-2 whitespace-pre-wrap break-keep text-sm text-ink">
                  {row.content ? <ContentWithImages content={row.content} images={requestImages} /> : '(내용 없음)'}
                </div>
              </>
            )}

            {(!!requestFiles.length || !!requestLinks.length) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {requestFiles.filter((f) => !inlineUsed.has(f.improvement_file_id)).map((f) => (
                  <FileChip
                    key={f.improvement_file_id}
                    file={f}
                    canRemove={isOwner || isAdmin}
                    onRemove={() => removeFile(f.improvement_file_id)}
                  />
                ))}
                {requestLinks.map((l) => (
                  <LinkChip
                    key={l.improvement_link_id}
                    link={l}
                    canRemove={isOwner || isAdmin}
                    onRemove={() => removeLink(l.improvement_link_id)}
                  />
                ))}
              </div>
            )}

            <div className="mt-3 flex gap-2">
              {canEdit && !editing && (
                <button
                  className="btn-ghost"
                  onClick={() => {
                    setEditTitle(row.title);
                    setEditContent(row.content);
                    setEditMenu(row.menu ?? '');
                    setEditing(true);
                  }}
                >
                  ✎ 내용 수정
                </button>
              )}
              {isOwner && !canEdit && !editing && (
                <span className="text-xs text-ink-faint">
                  담당자가 확인을 시작해 본문은 잠겼습니다 — 덧붙일 내용은 아래 코멘트로 남겨 주세요.
                </span>
              )}
              {isAdmin && (
                <button className="btn-ghost ml-auto text-red-600" onClick={removePost} disabled={busy}>
                  글 삭제
                </button>
              )}
            </div>

            {row.status === 'done' && (row.result_note || resultFiles.length > 0 || resultLinks.length > 0) && (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-sm font-semibold text-emerald-800">✅ 처리 결과</div>
                {row.result_note && (
                  <p className="mt-1 whitespace-pre-wrap break-keep text-sm text-emerald-900">{row.result_note}</p>
                )}
                {(!!resultFiles.length || !!resultLinks.length) && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {resultFiles.map((f) => (
                      <FileChip
                        key={f.improvement_file_id}
                        file={f}
                        canRemove={isAdmin}
                        onRemove={() => removeFile(f.improvement_file_id)}
                      />
                    ))}
                    {resultLinks.map((l) => (
                      <LinkChip
                        key={l.improvement_link_id}
                        link={l}
                        canRemove={isAdmin}
                        onRemove={() => removeLink(l.improvement_link_id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            <Timeline
              data={data}
              isAdmin={isAdmin}
              filesOfHistory={filesOfHistory}
              linksOfHistory={linksOfHistory}
              onChanged={load}
            />

            <div className="mt-4 border-t border-line pt-4">
              <textarea
                className="field h-24"
                placeholder="답변·추가 설명을 적어 주세요. 캡처는 Ctrl+V 로 붙여 넣을 수 있습니다."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onPaste={onPaste}
              />
              <PendingFiles files={files} onDrop={(i) => setFiles((p) => p.filter((_, k) => k !== i))} />
              <PendingLinks links={links} onDrop={(i) => setLinks((p) => p.filter((_, k) => k !== i))} />

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <label className="btn-ghost cursor-pointer">
                  📎 첨부
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      addFiles(Array.from(e.target.files ?? []));
                      e.target.value = '';
                    }}
                  />
                </label>
                <LinkComposer onAdd={(l) => setLinks((p) => [...p, l])} />

                {!!statusOptions.length && (
                  <select
                    className="field w-44"
                    value={statusTo}
                    onChange={(e) => setStatusTo(e.target.value as ImproveStatus | '')}
                  >
                    <option value="">상태 그대로</option>
                    {statusOptions.map((s) => (
                      <option key={s} value={s}>
                        {IMPROVE_STATUS[s].label}(으)로 변경
                      </option>
                    ))}
                  </select>
                )}

                <button className="btn-primary ml-auto" onClick={send} disabled={busy}>
                  {busy ? '올리는 중…' : '등록'}
                </button>
                <button className="btn-ghost" onClick={onClose} disabled={busy}>
                  닫기
                </button>
              </div>

              {statusTo === 'done' && isAdmin && (
                <textarea
                  className="field mt-2 h-20"
                  placeholder="처리 결과 — 요청자가 상단에서 바로 보는 문장입니다. 반영 내용을 사람 말로 적어 주세요."
                  value={resultNote}
                  onChange={(e) => setResultNote(e.target.value)}
                />
              )}
              {error && <div className="mt-2"><ErrorBox message={error} /></div>}
            </div>
          </>
        )}
      </DetailShell>
    </Modal>
  );
}

/** 코멘트와 상태 전이를 한 줄기로 — 두 축을 나눠 보이면 «무엇 때문에 상태가 바뀌었는지»가 끊긴다. */
function Timeline({
  data,
  isAdmin,
  filesOfHistory,
  linksOfHistory,
  onChanged,
}: {
  data: ImproveDetail | null;
  isAdmin: boolean;
  filesOfHistory: (hid: number) => ImproveFile[];
  linksOfHistory: (hid: number) => ImproveLink[];
  onChanged: () => void;
}) {
  const [editId, setEditId] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const history = data?.history ?? [];
  if (!history.length) return null;

  async function saveEdit(hid: number, byEmail: string) {
    setBusy(true);
    try {
      // 지문(작성자)을 같이 보낸다 — 화면을 연 뒤 그 줄이 바뀌었으면 서버가 막는다.
      await api.editImprovementComment({ historyId: hid, comment: draft.trim(), expectByEmail: byEmail });
      setEditId(null);
      onChanged();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 space-y-3 border-t border-line pt-4">
      {history.map((h) => (
        <div key={h.improvement_history_id} className="flex gap-3">
          <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-500" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 text-xs text-ink-soft">
              <span className="font-semibold text-ink">{h.by_name ?? h.by_email}</span>
              <span>{wallClock(h.created_at)}</span>
              {h.status_to && <StatusChip status={h.status_to} />}
              {h.edited_at && <span className="text-ink-faint">(수정됨)</span>}
              {isAdmin && editId !== h.improvement_history_id && h.comment && (
                <button
                  className="text-ink-faint hover:underline"
                  onClick={() => {
                    setEditId(h.improvement_history_id);
                    setDraft(h.comment ?? '');
                  }}
                >
                  ✎
                </button>
              )}
            </div>
            {editId === h.improvement_history_id ? (
              <div className="mt-1 space-y-2">
                <textarea className="field h-20" value={draft} onChange={(e) => setDraft(e.target.value)} />
                <div className="flex gap-2">
                  <button className="btn-ghost" onClick={() => setEditId(null)} disabled={busy}>
                    되돌리기
                  </button>
                  <button
                    className="btn-primary"
                    onClick={() => saveEdit(h.improvement_history_id, h.by_email)}
                    disabled={busy}
                  >
                    저장
                  </button>
                </div>
              </div>
            ) : (
              h.comment && <p className="mt-1 whitespace-pre-wrap break-keep text-sm">{h.comment}</p>
            )}
            {(!!filesOfHistory(h.improvement_history_id).length || !!linksOfHistory(h.improvement_history_id).length) && (
              <div className="mt-2 flex flex-wrap gap-2">
                {filesOfHistory(h.improvement_history_id).map((f) => (
                  <FileChip key={f.improvement_file_id} file={f} canRemove={false} onRemove={() => {}} />
                ))}
                {linksOfHistory(h.improvement_history_id).map((l) => (
                  <LinkChip key={l.improvement_link_id} link={l} canRemove={false} onRemove={() => {}} />
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
